import apiClient from '../../shared/services/apiClient';

/**
 * Webmail Service (User Version)
 *
 * Handles user-side webmail domain and email user management.
 * Users can manage their own mail domains and mail users.
 *
 * All routes use /api/user/webmail/* prefix
 */
class WebmailUserService {
  // ==========================================
  // Domain Management
  // ==========================================

  /**
   * Get user's mail domains
   */
  async getDomains() {
    try {
      const response = await apiClient.get('/api/user/webmail/domains');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get domain details
   */
  async getDomain(id) {
    try {
      const response = await apiClient.get(`/api/user/webmail/domains/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Assign subscription to a legacy domain (subscription_id=NULL)
   * @param {number} domainId - Domain ID
   * @param {object} data - { product_id, billing_cycle }
   */
  async assignSubscription(domainId, data) {
    try {
      const response = await apiClient.post(`/api/user/webmail/domains/${domainId}/assign-subscription`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete a mail domain (soft delete + remote delete)
   * @param {number} id - Domain ID
   */
  async deleteDomain(id) {
    try {
      const response = await apiClient.delete(`/api/user/webmail/domains/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get domain DNS records
   */
  async getDomainDns(id) {
    try {
      const response = await apiClient.get(`/api/user/webmail/domains/${id}/dns`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Phone / desktop mail client setup (settings + autoconfig DNS records)
   */
  async getClientSetup(id) {
    try {
      const response = await apiClient.get(`/api/user/webmail/domains/${id}/client-setup`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async _call(method, url, data) {
    try {
      const response = await apiClient[method](url, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  getAliasDomains(id) { return this._call('get', `/api/user/webmail/domains/${id}/alias-domains`); }
  getCatchAll(id) { return this._call('get', `/api/user/webmail/domains/${id}/catch-all`); }
  getBimi(id) { return this._call('get', `/api/user/webmail/domains/${id}/bimi`); }
  saveBimi(id, payload) { return this._call('post', `/api/user/webmail/domains/${id}/bimi`, payload); }
  deleteBimi(id) { return this._call('delete', `/api/user/webmail/domains/${id}/bimi`); }
  setCatchAll(id, destination) { return this._call('put', `/api/user/webmail/domains/${id}/catch-all`, { destination }); }
  addAliasDomain(id, domain) { return this._call('post', `/api/user/webmail/domains/${id}/alias-domains`, { domain }); }
  verifyAliasDomain(id, aid) { return this._call('post', `/api/user/webmail/domains/${id}/alias-domains/${aid}/verify`); }
  addAliasDomainDns(id, aid) { return this._call('post', `/api/user/webmail/domains/${id}/alias-domains/${aid}/dns`); }
  deleteAliasDomain(id, aid) { return this._call('delete', `/api/user/webmail/domains/${id}/alias-domains/${aid}`); }
  getSenderLists(id) { return this._call('get', `/api/user/webmail/domains/${id}/sender-lists`); }
  addSenderList(id, list, value) { return this._call('post', `/api/user/webmail/domains/${id}/sender-lists`, { list, value }); }
  deleteSenderList(id, eid) { return this._call('delete', `/api/user/webmail/domains/${id}/sender-lists/${eid}`); }

  async addClientSetupDns(id) {
    try {
      const response = await apiClient.post(`/api/user/webmail/domains/${id}/client-setup/dns`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Verify DNS records
   */
  async verifyDns(id) {
    try {
      const response = await apiClient.post(`/api/user/webmail/domains/${id}/verify-dns`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Generate DKIM key for domain
   */
  async generateDkim(id) {
    try {
      const response = await apiClient.post(`/api/user/webmail/domains/${id}/dkim`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Mail User Management
  // ==========================================

  /**
   * Get mail users
   * @param {object} params - { domain_id }
   */
  async getUsers(params = {}) {
    try {
      const response = await apiClient.get('/api/user/webmail/users', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new mail user
   * @param {object} data - { domain_id, username, password, display_name, quota_bytes, admin }
   */
  async createUser(data) {
    try {
      const response = await apiClient.post('/api/user/webmail/users', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update mail user
   */
  async updateUser(id, data) {
    try {
      const response = await apiClient.put(`/api/user/webmail/users/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete mail user
   */
  async deleteUser(id) {
    try {
      const response = await apiClient.delete(`/api/user/webmail/users/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Reset mail user password
   */
  async resetPassword(id, password) {
    try {
      const response = await apiClient.post(`/api/user/webmail/users/${id}/reset-password`, { password });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get login token for mail user (to open webmail)
   * @param {number} id - Local mail user ID
   */
  async getLoginToken(id) {
    try {
      const response = await apiClient.post(`/api/user/webmail/users/${id}/login`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Mail Alias Management
  // ==========================================

  /**
   * Get mail aliases for a domain
   * @param {object} params - { domain_id }
   * @returns response data with { aliases: [], usage: { current, max, remaining } }
   */
  async getAliases(params = {}) {
    try {
      const response = await apiClient.get('/api/user/webmail/aliases', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new mail alias
   * @param {object} data - { domain_id, username (local-part), destination }
   */
  async createAlias(data) {
    try {
      const response = await apiClient.post('/api/user/webmail/aliases/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update alias destinations (external targets need the owner's confirmation)
   * @param {object} data - { domain_id, alias_id, destination }
   */
  async updateAlias(data) {
    try {
      const response = await apiClient.post('/api/user/webmail/aliases/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Resend the forwarding confirmation mail
   * @param {object} data - { domain_id, alias_id, address }
   */
  async resendAliasVerify(data) {
    try {
      const response = await apiClient.post('/api/user/webmail/aliases/resend-verify', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete a mail alias
   * @param {object} data - { domain_id, alias_id }
   */
  async deleteAlias(data) {
    try {
      const response = await apiClient.post('/api/user/webmail/aliases/delete', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
}

export default new WebmailUserService();
